"use client";

import React from "react";

import { useTranslations } from "next-intl";

import { cn } from "@/utils/cn";

import styles from "../style.module.scss";
import { hasValue, getFallbackValue } from "../utils/fieldUtils";

interface ConditionalFieldProps {
  /** Значение поля */
  value: any;
  /** Ключ для перевода лейбла */
  labelKey: string;
  /** Кастомный лейбл (если не нужен перевод) */
  customLabel?: string;
  /** CSS классы для контейнера */
  className?: string;
  /** CSS классы для лейбла */
  labelClassName?: string;
  /** CSS классы для значения */
  valueClassName?: string;
  /** Кастомная функция рендера значения */
  renderValue?: (value: any) => React.ReactNode;
  /** Показывать ли поле, даже если значение пустое */
  forceShow?: boolean;
  /** Placeholder для пустого значения */
  emptyPlaceholder?: string;
  /** Дополнительные атрибуты */
  "data-testid"?: string;
}

/**
 * ConditionalField - Компонент для условного отображения полей
 *
 * Особенности:
 * - Автоматически скрывает поля с пустыми значениями
 * - Поддержка переводов для лейблов
 * - Кастомный рендеринг значений
 * - Fallback для пустых значений
 * - Консистентный дизайн
 */
export const ConditionalField: React.FC<ConditionalFieldProps> = ({
  value,
  labelKey,
  customLabel,
  className,
  labelClassName,
  valueClassName,
  renderValue,
  forceShow = false,
  emptyPlaceholder,
  "data-testid": testId,
}) => {
  const t = useTranslations();

  const hasRealValue = value !== null && value !== undefined && value !== "";

  // Don't render if no value and not forced to show
  if (!hasRealValue && !forceShow) {
    return null;
  }

  const label = customLabel || t(labelKey);
  const displayValue = renderValue
    ? renderValue(value)
    : hasRealValue
      ? value
      : emptyPlaceholder || t("common.notSpecified");

  return (
    <div
      className={className}
      data-testid={testId}
    >
      {label &&
        <div
          className={cn(
            labelClassName,
            "font-normal text-[16px] text-[#5B5B5B] mb-[10px]",
          )}
        >
          {label}
        </div>
      }
      <div
        className={cn(
          valueClassName,
          !hasRealValue && styles.emptyValue,
          "font-medium text-[16px] text-[#323232]",
        )}
      >
        {displayValue}
      </div>
    </div>
  );
};

export default ConditionalField;
